import { fromJS } from 'immutable';

/**
 * Find product in catalog by code and segment
 * @param {Object} params - Parameters object
 * @param {string} params.code - Product code
 * @param {string} params.segment - Segment: 'outgoing' or 'incoming'
 * @param {List} params.catalogOutgoing - Immutable List of outgoing products
 * @param {List} params.catalogIncoming - Immutable List of incoming products
 * @returns {Map|undefined} Product from catalog or undefined
 */
function findProductInCatalog({ code, segment, catalogOutgoing, catalogIncoming }) {
  const catalogSegment = segment === 'outgoing' ? catalogOutgoing : catalogIncoming;
  return catalogSegment.find((product) => product.get('code') === code);
}

/**
 * Transform a single ancillary to product format
 * @param {Object} params - Parameters object
 * @param {Map} params.ancillary - Immutable ancillary Map with code and quantity
 * @param {string} params.segment - Segment: 'outgoing' or 'incoming'
 * @param {List} params.catalogOutgoing - Immutable List of outgoing products
 * @param {List} params.catalogIncoming - Immutable List of incoming products
 * @returns {Map} Immutable product Map with full information
 */
function transformAncillaryToProduct({ ancillary, segment, catalogOutgoing, catalogIncoming }) {
  const code = ancillary.get('code');
  const quantity = ancillary.get('quantity') || 0;
  const catalogProduct = findProductInCatalog({
    code,
    segment,
    catalogOutgoing,
    catalogIncoming,
  });

  if (catalogProduct) {
    // Product found in catalog, use full information
    const stock = catalogProduct.get('stock') || 0;
    return fromJS({
      code,
      name: catalogProduct.get('name') || '',
      price: catalogProduct.get('price') || 0,
      stock,
      image: catalogProduct.get('image') || '',
      imageFormat: catalogProduct.get('imageFormat') || '',
      description: catalogProduct.get('description') || '',
      quantity,
    });
  }

  // Product not found in catalog, use minimal information
  // This can happen if catalog hasn't loaded yet or product was removed
  return fromJS({
    code,
    name: '',
    price: 0,
    stock: 0,
    image: '',
    imageFormat: '',
    description: '',
    quantity,
  });
}

/**
 * Transform ancillaries array to products array
 * @param {Object} params - Parameters object
 * @param {List} params.ancillaries - Immutable List of ancillaries
 * @param {string} params.segment - Segment: 'outgoing' or 'incoming'
 * @param {List} params.catalogOutgoing - Immutable List of outgoing products
 * @param {List} params.catalogIncoming - Immutable List of incoming products
 * @returns {List} Immutable List of products
 */
function transformAncillariesToProducts({
  ancillaries,
  segment,
  catalogOutgoing,
  catalogIncoming,
}) {
  if (!ancillaries || ancillaries.size === 0) {
    return fromJS([]);
  }

  return ancillaries.map((ancillary) => {
    return transformAncillaryToProduct({ ancillary, segment, catalogOutgoing, catalogIncoming });
  });
}

/**
 * Enrich a single product with catalog information
 * @param {Object} params - Parameters object
 * @param {Map} params.product - Immutable product Map
 * @param {string} params.segment - Segment: 'outgoing' or 'incoming'
 * @param {List} params.catalogOutgoing - Immutable List of outgoing products
 * @param {List} params.catalogIncoming - Immutable List of incoming products
 * @returns {Map} Enriched product Map
 */
function enrichProduct({ product, segment, catalogOutgoing, catalogIncoming }) {
  const code = product.get('code');
  const catalogProduct = findProductInCatalog({
    code,
    segment,
    catalogOutgoing,
    catalogIncoming,
  });

  if (catalogProduct) {
    // Enrich with catalog information, preserving quantity and updating other fields
    const stock = catalogProduct.get('stock') || 0;
    return product
      .set('name', catalogProduct.get('name') || '')
      .set('price', catalogProduct.get('price') || 0)
      .set('stock', stock)
      .set('image', catalogProduct.get('image') || '')
      .set('imageFormat', catalogProduct.get('imageFormat') || '')
      .set('description', catalogProduct.get('description') || '');
  }

  // Product not found in catalog, return as is
  return product;
}

/**
 * Transform ancillaries from purchase passengers to selectedProducts format
 * Converts passenger ancillaries (outgoingAncillaries/incomingAncillaries) to
 * the selectedProducts structure used in productsCart reducer
 *
 * @param {Object} params - Parameters object
 * @param {List|Map} params.passengers - Immutable passengers list from purchase state
 * @param {Map} params.catalog - Immutable catalog Map with outgoing/incoming arrays
 * @returns {Map} Immutable Map of selectedProducts structure: { passengerIndex: { outgoing: [...], incoming: [...] } }
 *
 * @example
 * Input:
 *   passengers = [{ outgoingAncillaries: [{code: 'BEB001', quantity: 2}], incomingAncillaries: [{code: 'SNK002', quantity: 1}] }]
 *   catalog = { outgoing: [{code: 'BEB001', name: 'Coca Cola', price: 45, stock: 120, ...}], incoming: [...] }
 * Output:
 *   { 0: { outgoing: [{code: 'BEB001', name: 'Coca Cola', price: 45, quantity: 2, stock: 120, ...}], incoming: [...] } }
 */
function transformAncillariesToSelectedProducts({ passengers, catalog }) {
  if (!passengers || passengers.size === 0) {
    return fromJS({});
  }

  const catalogOutgoing = catalog?.get('outgoing') || fromJS([]);
  const catalogIncoming = catalog?.get('incoming') || fromJS([]);

  const selectedProducts = {};

  passengers.forEach((passenger, index) => {
    const outgoingAncillaries = passenger.get('outgoingAncillaries');
    const incomingAncillaries = passenger.get('incomingAncillaries');

    const outgoingProducts = transformAncillariesToProducts({
      ancillaries: outgoingAncillaries,
      segment: 'outgoing',
      catalogOutgoing,
      catalogIncoming,
    });

    const incomingProducts = transformAncillariesToProducts({
      ancillaries: incomingAncillaries,
      segment: 'incoming',
      catalogOutgoing,
      catalogIncoming,
    });

    // Only add passenger entry if there are products in at least one segment
    if (outgoingProducts.size > 0 || incomingProducts.size > 0) {
      selectedProducts[String(index)] = {
        outgoing: outgoingProducts.toJS(),
        incoming: incomingProducts.toJS(),
      };
    }
  });

  return fromJS(selectedProducts);
}

/**
 * Enrich selectedProducts with catalog information when catalog is loaded
 * Updates products that have incomplete information (missing name, price, etc.)
 * with full details from the catalog
 *
 * @param {Object} params - Parameters object
 * @param {Map} params.selectedProducts - Immutable Map of selectedProducts
 * @param {Map} params.catalog - Immutable catalog Map with outgoing/incoming arrays
 * @returns {Map} Updated selectedProducts with enriched information
 */
export function enrichSelectedProductsWithCatalog({ selectedProducts, catalog }) {
  if (!selectedProducts || selectedProducts.size === 0) {
    return selectedProducts;
  }

  const catalogOutgoing = catalog?.get('outgoing') || fromJS([]);
  const catalogIncoming = catalog?.get('incoming') || fromJS([]);

  // Enrich each passenger's products
  let enrichedSelectedProducts = selectedProducts;

  selectedProducts.forEach((passengerProducts, passengerIndex) => {
    const outgoing = passengerProducts.get('outgoing');
    const incoming = passengerProducts.get('incoming');

    if (outgoing && outgoing.size > 0) {
      const enrichedOutgoing = outgoing.map((product) => {
        return enrichProduct({ product, segment: 'outgoing', catalogOutgoing, catalogIncoming });
      });
      enrichedSelectedProducts = enrichedSelectedProducts.setIn(
        [passengerIndex, 'outgoing'],
        enrichedOutgoing,
      );
    }

    if (incoming && incoming.size > 0) {
      const enrichedIncoming = incoming.map((product) => {
        return enrichProduct({ product, segment: 'incoming', catalogOutgoing, catalogIncoming });
      });
      enrichedSelectedProducts = enrichedSelectedProducts.setIn(
        [passengerIndex, 'incoming'],
        enrichedIncoming,
      );
    }
  });

  return enrichedSelectedProducts;
}

export default transformAncillariesToSelectedProducts;
